class EsimLocalizationSwitcher extends HTMLElement {
  connectedCallback() {
    if (this._connected) return;

    this._trigger = this.querySelector('[data-esim-trigger]');
    this._dialog = this.querySelector('[data-esim-dialog]');
    this._closeButton = this.querySelector('[data-esim-close]');
    this._form = this.querySelector('.esim-localization-switcher__form');
    this._tabs = Array.from(this.querySelectorAll('[data-esim-tab]'));
    if (!this._trigger || !this._dialog || !this._closeButton || !this._form) return;

    this._onTriggerClick = this.open.bind(this);
    this._onCloseClick = this.close.bind(this);
    this._onDialogClick = this.handleDialogClick.bind(this);
    this._onDialogClose = this.handleDialogClose.bind(this);
    this._onOptionChange = this.handleOptionChange.bind(this);
    this._onTabClick = this.handleTabClick.bind(this);
    this._onTabKeydown = this.handleTabKeydown.bind(this);
    this._onDialogKeyup = this.handleDialogKeyup.bind(this);

    this._trigger.addEventListener('click', this._onTriggerClick);
    this._closeButton.addEventListener('click', this._onCloseClick);
    this._dialog.addEventListener('click', this._onDialogClick);
    this._dialog.addEventListener('close', this._onDialogClose);
    this._dialog.addEventListener('keyup', this._onDialogKeyup);
    this._form.addEventListener('change', this._onOptionChange);
    this._tabs.forEach((tab) => {
      tab.addEventListener('click', this._onTabClick);
      tab.addEventListener('keydown', this._onTabKeydown);
    });

    this._trigger.hidden = false;
    this._connected = true;
  }

  disconnectedCallback() {
    if (!this._connected) return;

    this._trigger.removeEventListener('click', this._onTriggerClick);
    this._closeButton.removeEventListener('click', this._onCloseClick);
    this._dialog.removeEventListener('click', this._onDialogClick);
    this._dialog.removeEventListener('close', this._onDialogClose);
    this._dialog.removeEventListener('keyup', this._onDialogKeyup);
    this._form.removeEventListener('change', this._onOptionChange);
    this._tabs.forEach((tab) => {
      tab.removeEventListener('click', this._onTabClick);
      tab.removeEventListener('keydown', this._onTabKeydown);
    });

    this._trigger.hidden = true;
    this._connected = false;
  }

  open() {
    if (this._dialog.open) return;

    if (typeof this._dialog.showModal === 'function') {
      this._dialog.showModal();
    } else {
      this._dialog.setAttribute('open', '');
    }

    this._trigger.setAttribute('aria-expanded', 'true');
    this._tabs.find((tab) => tab.getAttribute('aria-selected') === 'true')?.focus();
  }

  close() {
    if (typeof this._dialog.close === 'function' && this._dialog.open) {
      this._dialog.close();
    } else {
      this._dialog.removeAttribute('open');
      this.handleDialogClose();
    }
  }

  handleDialogClick(event) {
    if (event.target === this._dialog) this.close();
  }

  handleDialogClose() {
    this._trigger.setAttribute('aria-expanded', 'false');
    this._trigger.focus();
  }

  handleDialogKeyup(event) {
    if (event.key === 'Escape') event.stopPropagation();
  }

  handleOptionChange(event) {
    if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-esim-option]')) return;

    if (typeof this._form.requestSubmit === 'function') {
      this._form.requestSubmit();
    } else {
      this._form.submit();
    }
  }

  handleTabClick(event) {
    this.activateTab(event.currentTarget);
  }

  handleTabKeydown(event) {
    const currentIndex = this._tabs.indexOf(event.currentTarget);
    let nextIndex = currentIndex;

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = (currentIndex + 1) % this._tabs.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = (currentIndex - 1 + this._tabs.length) % this._tabs.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = this._tabs.length - 1;
    } else {
      return;
    }

    event.preventDefault();
    this.activateTab(this._tabs[nextIndex]);
  }

  activateTab(activeTab) {
    const activeName = activeTab.dataset.esimTab;

    this._tabs.forEach((tab) => {
      const selected = tab === activeTab;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    });

    this.querySelectorAll('[data-esim-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.esimPanel !== activeName;
    });

    activeTab.focus();
  }
}

if (!customElements.get('esim-localization-switcher')) {
  customElements.define('esim-localization-switcher', EsimLocalizationSwitcher);
}
